import React, { useRef, useState } from 'react';
import { Upload, Trash2, AlertTriangle, CheckCircle2, ExternalLink } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;
const _auth = () => ({ Authorization: `Bearer ${localStorage.getItem('ratnatraya_token')}` });

/**
 * Director Other Companies — Section 167(1) Impact Map
 *
 * Lets admin upload one MCA "Director Data" .xlsx per director on this lead.
 * Each upload is parsed server-side, classified by Section 167(1) impact, and
 * shown as an expandable card with: counter strip, current-case cross-link,
 * per-row "Suspected 3-yr non-filing" flag + admin note textarea.
 *
 * Iter 90 — Phase 1.
 */
export function DirectorOtherCompanies({ lead, onRefresh, apiBase = '/ccfs-leads' }) {
  const records = lead.director_other_companies || [];
  const mcaDirs = lead.mca_directors || [];
  const uploadedDins = new Set(records.map(r => r.din));
  const mcaWithDin = mcaDirs.filter(d => d.din_pan);
  const fileRef = useRef(null);

  const handleUpload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append('file', file);
    try {
      const res = await fetch(`${API}/api${apiBase}/${lead.id}/director-data/upload`, {
        method: 'POST', headers: _auth(), body: fd,
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Upload failed');
      toast.success(`Parsed ${data.companies_count} companies + ${data.llps_count} LLPs for DIN ${data.din}`);
      onRefresh?.();
    } catch (e) {
      toast.error(e.message);
    }
  };

  const handleDelete = async (din) => {
    if (!window.confirm(`Remove parsed data for DIN ${din}?`)) return;
    await fetch(`${API}/api${apiBase}/${lead.id}/director-data/${din}`, {
      method: 'DELETE', headers: _auth(),
    });
    toast.success('Director removed');
    onRefresh?.();
  };

  return (
    <div
      className="bg-white border border-slate-200 rounded-xl p-4 space-y-3"
      data-testid={`director-other-companies-${lead.id}`}
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <AlertTriangle className="w-4 h-4 text-amber-600" />
          <h4 className="text-sm font-semibold text-slate-900">
            Director Other Companies — Section 167(1) Impact Map
          </h4>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-[11px] font-medium px-2 py-1 rounded-full bg-slate-100 text-slate-700">
            {records.length} of {mcaWithDin.length || '?'} directors uploaded
          </span>
          <input
            ref={fileRef}
            type="file"
            accept=".xlsx,.xls"
            className="hidden"
            onChange={(e) => { handleUpload(e.target.files?.[0]); e.target.value = ''; }}
            data-testid={`upload-director-data-input-${lead.id}`}
          />
          <button
            onClick={() => fileRef.current?.click()}
            className="text-[11px] font-medium px-3 py-1.5 rounded-lg bg-indigo-600 hover:bg-indigo-700 text-white inline-flex items-center gap-1.5"
            data-testid={`upload-director-data-btn-${lead.id}`}
          >
            <Upload className="w-3.5 h-3.5" /> Upload Director Data .xlsx
          </button>
        </div>
      </div>

      {/* MCA directors checklist — each pill shows upload status; click an "amber" pill to upload that director's file */}
      {mcaWithDin.length > 0 && (
        <div className="text-[11px] bg-slate-50 border border-slate-200 rounded-lg p-2.5 flex flex-wrap gap-2 items-center">
          <span className="font-medium text-slate-600 mr-1">Directors per MCA:</span>
          {mcaWithDin.map((d, i) => {
            const done = uploadedDins.has(d.din_pan);
            return (
              <button
                key={d.din_pan || i}
                type="button"
                disabled={done}
                onClick={() => !done && fileRef.current?.click()}
                title={done ? 'Director data uploaded' : 'Click to upload this director\'s MCA "Director Data" .xlsx'}
                className={`inline-flex items-center gap-1 px-2 py-1 rounded-full border text-[10px] font-medium ${done
                  ? 'bg-emerald-50 border-emerald-200 text-emerald-700 cursor-default'
                  : 'bg-amber-50 border-amber-300 text-amber-800 hover:bg-amber-100 cursor-pointer'}`}
                data-testid={`mca-director-pip-${d.din_pan}`}
              >
                {done
                  ? <CheckCircle2 className="w-3 h-3" />
                  : <Upload className="w-3 h-3" />}
                <span>{d.name} ({d.din_pan})</span>
              </button>
            );
          })}
        </div>
      )}

      {records.length === 0 && (
        <p className="text-xs text-slate-500 italic">
          No director data uploaded yet. Upload one MCA &quot;Director Data&quot; .xlsx per director to map
          Section 167(1) disqualification impact across their other directorships.
        </p>
      )}

      {records.map(rec => (
        <DirectorCard key={rec.din} lead={lead} record={rec} onRefresh={onRefresh} onDelete={() => handleDelete(rec.din)} apiBase={apiBase} />
      ))}
    </div>
  );
}

function DirectorCard({ lead, record, onRefresh, onDelete, apiBase = '/ccfs-leads' }) {
  const [open, setOpen] = useState(true);
  const rs = record.risk_summary || {};
  const totalImpact = rs.section_167_1_impact || 0;
  return (
    <div className="border border-slate-200 rounded-lg overflow-hidden">
      <button
        type="button"
        onClick={() => setOpen(!open)}
        className="w-full flex items-center justify-between gap-3 px-3 py-2.5 bg-gradient-to-r from-slate-50 to-white hover:from-slate-100"
        data-testid={`director-card-toggle-${record.din}`}
      >
        <div className="text-left flex-1">
          <p className="text-sm font-semibold text-slate-900">{record.name || '—'}</p>
          <p className="text-[10px] font-mono text-slate-500">DIN {record.din}</p>
        </div>
        <div className="flex items-center gap-1.5 flex-wrap">
          <Pill color="rose" label={`${rs.active_companies || 0} Active`} testId={`pill-active-${record.din}`} />
          <Pill color="amber" label={`${rs.striking_off || 0} Strike-off`} />
          <Pill color="slate" label={`${rs.ceased || 0} Ceased`} />
          {(rs.active_llps || rs.ceased_llps) ? (
            <Pill color="indigo" label={`${rs.active_llps || 0}A / ${rs.ceased_llps || 0}C LLP`} />
          ) : null}
        </div>
      </button>

      {open && (
        <div className="p-3 space-y-3 bg-white">
          {/* Section 167(1) callout */}
          <div
            className={`rounded-lg p-2.5 text-[11px] border ${totalImpact > 0
              ? 'bg-rose-50 border-rose-200 text-rose-800'
              : 'bg-emerald-50 border-emerald-200 text-emerald-800'}`}
            data-testid={`s167-impact-${record.din}`}
          >
            <strong>Section 167(1) impact:</strong>{' '}
            {totalImpact > 0
              ? `If disqualified under Section 164(2), ${totalImpact} active mandate${totalImpact === 1 ? '' : 's'} would be vacated immediately (${rs.active_companies || 0} compan${rs.active_companies === 1 ? 'y' : 'ies'} + ${rs.active_llps || 0} LLP${rs.active_llps === 1 ? '' : 's'}).`
              : `No active mandates — director disqualification would have no spillover impact.`}
          </div>

          {/* Companies table */}
          {record.companies?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-700 mb-1.5">Other Directorships ({record.companies.length})</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px]">
                    <tr>
                      <th className="text-left px-2.5 py-1.5">CIN</th>
                      <th className="text-left px-2.5 py-1.5">Company</th>
                      <th className="text-left px-2.5 py-1.5">Status</th>
                      <th className="text-left px-2.5 py-1.5">Role</th>
                      <th className="text-left px-2.5 py-1.5">Appointed</th>
                      <th className="text-left px-2.5 py-1.5">Ceased</th>
                      <th className="text-center px-2.5 py-1.5">Suspected 3-yr Gap</th>
                      <th className="text-left px-2.5 py-1.5 w-48">Internal Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.companies.map((c, i) => (
                      <CompanyRow key={`${c.cin}-${i}`} lead={lead} din={record.din} row={c} onRefresh={onRefresh} idKey="cin" apiBase={apiBase} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {/* LLPs table */}
          {record.llps?.length > 0 && (
            <div>
              <p className="text-[11px] font-semibold text-slate-700 mb-1.5">LLP Partnerships ({record.llps.length})</p>
              <div className="border border-slate-200 rounded-lg overflow-hidden">
                <table className="w-full text-[11px]">
                  <thead className="bg-slate-50 text-slate-500 text-[10px]">
                    <tr>
                      <th className="text-left px-2.5 py-1.5">LLPIN</th>
                      <th className="text-left px-2.5 py-1.5">LLP</th>
                      <th className="text-left px-2.5 py-1.5">Status</th>
                      <th className="text-left px-2.5 py-1.5">Role</th>
                      <th className="text-left px-2.5 py-1.5">Appointed</th>
                      <th className="text-left px-2.5 py-1.5">Ceased</th>
                      <th className="text-center px-2.5 py-1.5">Suspected 3-yr Gap</th>
                      <th className="text-left px-2.5 py-1.5 w-48">Internal Note</th>
                    </tr>
                  </thead>
                  <tbody>
                    {record.llps.map((lp, i) => (
                      <CompanyRow key={`${lp.llpin}-${i}`} lead={lead} din={record.din} row={lp} onRefresh={onRefresh} idKey="llpin" apiBase={apiBase} />
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          <div className="flex justify-end">
            <button
              onClick={onDelete}
              className="text-[10px] text-rose-600 hover:text-rose-800 inline-flex items-center gap-1"
              data-testid={`delete-director-${record.din}`}
            >
              <Trash2 className="w-3 h-3" /> Remove this director&apos;s record
            </button>
          </div>
        </div>
      )}
    </div>
  );
}

function CompanyRow({ lead, din, row, onRefresh, idKey, apiBase = '/ccfs-leads' }) {
  const identifier = row[idKey];
  const [note, setNote] = useState(row.admin_note || '');
  const [suspected, setSuspected] = useState(!!row.suspected_3yr_non_filing);
  const riskColor = row.risk === 'high' ? 'rose' : row.risk === 'low' ? 'amber' : 'slate';

  const saveNote = async () => {
    if ((row.admin_note || '') === note) return;
    await fetch(`${API}/api${apiBase}/${lead.id}/director-data/${din}/note`, {
      method: 'PATCH',
      headers: { ..._auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ [idKey]: identifier, note }),
    });
    toast.success('Note saved');
    onRefresh?.();
  };

  const toggleFlag = async (e) => {
    const next = e.target.checked;
    setSuspected(next);
    const res = await fetch(`${API}/api${apiBase}/${lead.id}/director-data/${din}/flag`, {
      method: 'PATCH',
      headers: { ..._auth(), 'Content-Type': 'application/json' },
      body: JSON.stringify({ [idKey]: identifier, suspected: next }),
    });
    if (res.ok) {
      toast.success(next ? '3-yr gap flag set' : '3-yr gap flag cleared');
    } else {
      toast.error('Failed to update flag');
      setSuspected(!next);  // revert UI on failure
    }
    onRefresh?.();
  };

  return (
    <tr className={`border-t border-slate-100 ${row.is_current_case ? 'bg-indigo-50/50' : ''}`}>
      <td className="px-2.5 py-1.5 font-mono text-[10px] text-slate-700">
        {identifier}
        {row.is_current_case && (
          <span className="ml-1.5 inline-flex items-center gap-0.5 text-[9px] font-semibold text-indigo-700 bg-indigo-100 px-1.5 py-0.5 rounded-full">
            <ExternalLink className="w-2.5 h-2.5" /> Current Case
          </span>
        )}
      </td>
      <td className="px-2.5 py-1.5 text-slate-800">{row.name || '—'}</td>
      <td className="px-2.5 py-1.5">
        <Pill color={riskColor} label={row.status || '—'} />
      </td>
      <td className="px-2.5 py-1.5 text-slate-600">
        {row.designation || '—'}{row.category ? <span className="text-slate-400"> · {row.category}</span> : null}
      </td>
      <td className="px-2.5 py-1.5 text-slate-600">{row.appointment_original || row.appointment_current || '—'}</td>
      <td className="px-2.5 py-1.5 text-slate-600">{row.cessation || '—'}</td>
      <td className="px-2.5 py-1.5 text-center">
        <input
          type="checkbox"
          checked={suspected}
          onChange={toggleFlag}
          className="w-3.5 h-3.5 accent-rose-600"
          data-testid={`suspected-3yr-${din}-${identifier}`}
        />
      </td>
      <td className="px-2.5 py-1.5">
        <input
          type="text"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          onBlur={saveNote}
          placeholder="CA note…"
          maxLength={1000}
          className="w-full text-[10px] px-2 py-1 border border-slate-200 rounded focus:outline-none focus:border-indigo-400"
          data-testid={`director-note-${din}-${identifier}`}
        />
      </td>
    </tr>
  );
}

function Pill({ color = 'slate', label, testId }) {
  const styles = {
    rose: 'bg-rose-100 text-rose-700 border-rose-200',
    amber: 'bg-amber-100 text-amber-700 border-amber-200',
    slate: 'bg-slate-100 text-slate-600 border-slate-200',
    indigo: 'bg-indigo-100 text-indigo-700 border-indigo-200',
  }[color] || 'bg-slate-100 text-slate-600 border-slate-200';
  return (
    <span
      className={`inline-block text-[10px] font-medium px-2 py-0.5 rounded-full border ${styles}`}
      data-testid={testId}
    >
      {label}
    </span>
  );
}
